"use client";

import * as React from "react";
import { script } from "@/lib/helpers/script";
import type { ThemeProviderProps, UseThemeProps } from "@/lib/types";

const MEDIA = "(prefers-color-scheme: dark)";
const isServer = typeof window === "undefined";
const ThemeContext = React.createContext<UseThemeProps | undefined>(undefined);
const defaultContext: UseThemeProps = {
  setColorMode: (_) => {},
  setColorScheme: (_) => {},
  resolvedColorMode: "",
  colorMode: "",
  colorScheme: "",
};

const themes = [
  "dark",
  "light",
  "green-light",
  "green-dark",
  "violet-light",
  "violet-dark",
  "orange-light",
  "orange-dark",
  "rose-light",
  "rose-dark",
  "yellow-light",
  "yellow-dark",
  "blue-dark",
  "blue-light",
  "leaf-light",
  "leaf-dark",
  "ruby-light",
  "ruby-dark",
  "sand-light",
  "sand-dark",
  "pink-light",
  "pink-dark",
  "slate-light",
  "slate-dark",
  "teal-light",
  "teal-dark",
  "autumn-light",
  "autumn-dark",
  "crimson-light",
  "crimson-dark",
  "sky-light",
  "sky-dark",
  "skin-light",
  "skin-dark",
  "plum-dark",
  "plum-light",
  "darklime-light",
  "darklime-dark",
  "cyan-light",
  "cyan-dark",
  "amber-light",
  "amber-dark",
  "red-light",
  "red-dark",
  "ink-light",
  "ink-dark",
  "purple-light",
  "purple-dark",
];

export const useTheme = () => React.useContext(ThemeContext) ?? defaultContext;

export const QuranThemeProvider = (
  props: ThemeProviderProps,
): React.ReactNode => {
  const context = React.useContext(ThemeContext);

  if (context) return props.children;
  return <Theme {...props} />;
};

const Theme = ({
  enableSystem = true,
  storageKeys = { colorMode: "colorMode", colorScheme: "colorScheme" },
  defaultColorMode = enableSystem ? "system" : "dark",
  defaultColorScheme = "",
  children,
  nonce,
}: ThemeProviderProps) => {
  const [colorMode, setMode] = React.useState<string>(defaultColorMode);
  const [colorScheme, setScheme] = React.useState<string>(defaultColorScheme);
  const [resolvedColorMode, setResolvedColorMode] = React.useState<
    string | undefined
  >(() => getTheme(storageKeys.colorMode));

  const updateColorMode = React.useCallback(
    (mode: string, scheme: string) => {
      let resolved = mode || "system";
      if (mode === "system" && enableSystem) {
        resolved = getSystemTheme();
      }
      const name = resolved;
      const theme = scheme + name;
      console.log("name: ", name);
      const d = document.documentElement;

      d.classList.remove(...themes);
      if (name) d.classList.add(theme);
    },
    [enableSystem],
  );

  const setColorScheme = React.useCallback(
    (value: string | ((val: string) => void)) => {
      const newColorScheme =
        typeof value === "function" ? value(colorScheme)!! : value;
      setScheme(newColorScheme);
      localStorage.setItem(storageKeys.colorScheme, newColorScheme);
    },
    [colorScheme, storageKeys],
  );

  const setColorMode = React.useCallback(
    (value: string | ((val: string) => void)) => {
      const newColorMode =
        typeof value === "function" ? value(colorMode)!! : value;
      setMode(newColorMode);
      localStorage.setItem(storageKeys.colorMode, newColorMode);
    },
    [colorMode, storageKeys],
  );

  const handleMediaQuery = React.useCallback(
    (e: MediaQueryListEvent | MediaQueryList) => {
      const resolved = getSystemTheme(e);
      setResolvedColorMode(resolved);

      if (colorMode === "system" && enableSystem) {
        updateColorMode(resolved, colorScheme);
      }
    },
    [colorMode, updateColorMode, enableSystem, colorScheme],
  );

  React.useEffect(() => {
    const media = window.matchMedia(MEDIA);

    media.addEventListener("change", handleMediaQuery);
    handleMediaQuery(media);

    return () => media.removeEventListener("change", handleMediaQuery);
  }, [handleMediaQuery]);

  React.useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== storageKeys.colorMode) {
        return;
      }
      const colorMode = e.newValue || defaultColorMode;
      setColorMode(colorMode);
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [setColorMode, storageKeys, defaultColorMode]);

  React.useEffect(() => {
    updateColorMode(colorMode, colorScheme);
  }, [colorMode, updateColorMode, colorScheme]);

  const providerValue = {
    colorMode,
    setColorMode,
    colorScheme,
    setColorScheme,
    resolvedColorMode: colorMode === "system" ? resolvedColorMode : colorMode,
    resolvedTheme:
      colorMode === "system"
        ? `${resolvedColorMode}-${colorScheme}`
        : `${colorMode}-${colorScheme}`,
  };
  return (
    <ThemeContext.Provider value={providerValue}>
      {defaultColorMode == "system" && enableSystem && (
        <ThemeScript
          {...{
            storageKeys,
            enableSystem,
            defaultColorMode,
            defaultColorScheme,
            // nonce,
          }}
        />
      )}

      {children}
    </ThemeContext.Provider>
  );
};

const ThemeScript = React.memo(
  ({
    storageKeys,
    enableSystem,
    defaultColorMode,
    defaultColorScheme,
    // nonce,
  }: Omit<ThemeProviderProps, "children"> & { defaultColorMode: string }) => {
    const scriptArgs = JSON.stringify([
      MEDIA,
      themes,
      storageKeys,
      defaultColorMode,
      defaultColorScheme,
      enableSystem,
      themes,
    ]).slice(1, -1);

    return (
      <script
        // nonce={typeof window === "undefined" ? nonce : ""}
        dangerouslySetInnerHTML={{
          __html: `(${script.toString()})(${scriptArgs})`,
        }}
      />
    );
  },
);

const getTheme = (key: string, fallback?: string) => {
  if (isServer) return undefined;
  let theme;
  try {
    theme = localStorage.getItem(key) || undefined;
  } catch (e) {
    console.log("Somethng went wrong.");
  }
  return theme || fallback;
};

const getSystemTheme = (e?: MediaQueryList | MediaQueryListEvent) => {
  if (!e) e = window.matchMedia(MEDIA);
  const isDark = e.matches;
  const systemTheme = isDark ? "dark" : "light";
  return systemTheme;
};

Theme.displayName = "Theme";
QuranThemeProvider.displayName = "QuranThemeProvider";
ThemeScript.displayName = "ThemeScript";
